/**
 * Wallet Connection Component
 * Integrates @web3modal/ethers for wallet connection
 */

import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { createWeb3Modal, defaultConfig } from '@web3modal/ethers';
import { BrowserProvider } from 'ethers';

interface WalletConnectProps {
  onConnect: (address: string, provider: BrowserProvider) => void;
  onDisconnect: () => void;
}

// Polygon Mainnet only
const polygonMainnet = {
  chainId: 137,
  name: 'Polygon',
  currency: 'POL',
  explorerUrl: 'https://polygonscan.com',
  rpcUrl: import.meta.env.VITE_POLYGON_RPC_URL_MAINNET || 'https://polygon-rpc.com'
};

const projectId = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID || '';

const metadata = {
  name: 'IDRA Swap',
  description: 'Swap USDC/USDT for IDRA tokens',
  url: 'https://idra.padigital.id',
  icons: ['https://cdn.padigital.id/padigital-logo-light.png']
};

const ethersConfig = defaultConfig({
  metadata,
  enableEIP6963: true,
  enableInjected: true,
  enableCoinbase: true,
  auth: {
    email: false, // Disable email login
    socials: [], // Disable all social logins (empty array = no social providers)
    showWallets: true, // Show wallet options
    walletFeatures: true // Enable wallet features
  }
});

// Create Web3Modal - Polygon Mainnet only (exported for AdminPanel to get wallet provider)
export const modal = createWeb3Modal({
  ethersConfig,
  chains: [polygonMainnet],
  projectId,
  enableAnalytics: false,
  enableOnramp: false // Disable buy crypto option
});

export function WalletConnect({ onConnect, onDisconnect }: WalletConnectProps) {
  const [address, setAddress] = useState<string | null>(null);
  const [ensName, setEnsName] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);

  useEffect(() => {
    let isSubscribed = true;
    let isHandling = false;

    // Listen for account changes
    const handleAccountChange = async () => {
      if (!isSubscribed || isHandling) return;
      isHandling = true;

      try {
        const provider = modal.getWalletProvider();
        const isConnected = modal.getIsConnected();

        console.log('handleAccountChange:', { hasProvider: !!provider, isConnected });

        if (provider && isConnected) {
          // Ensure provider is EIP-1193 compatible
          if (!provider.request) {
            console.error('Provider is not EIP-1193 compatible');
            return;
          }

          const ethersProvider = new BrowserProvider(provider);

          // Check and switch to Polygon Mainnet if needed
          const network = await ethersProvider.getNetwork();
          const currentChainId = Number(network.chainId);

          if (currentChainId !== 137) {
            try {
              // Request network switch to Polygon Mainnet
              await provider.request({
                method: 'wallet_switchEthereumChain',
                params: [{ chainId: '0x89' }], // 137 in hex
              });
            } catch (switchError: any) {
              // This error code indicates that the chain has not been added to MetaMask
              if (switchError.code === 4902) {
                try {
                  await provider.request({
                    method: 'wallet_addEthereumChain',
                    params: [{
                      chainId: '0x89',
                      chainName: 'Polygon Mainnet',
                      nativeCurrency: {
                        name: 'POL',
                        symbol: 'POL',
                        decimals: 18
                      },
                      rpcUrls: [polygonMainnet.rpcUrl],
                      blockExplorerUrls: [polygonMainnet.explorerUrl]
                    }],
                  });
                } catch (addError) {
                  console.error('Failed to add Polygon Mainnet network:', addError);
                  alert('Please add Polygon Mainnet network to your wallet');
                  return;
                }
              } else {
                console.error('Failed to switch to Polygon Mainnet:', switchError);
                alert('Please switch to Polygon Mainnet network');
                return;
              }
            }
          }

          if (!isSubscribed) return;

          const signer = await ethersProvider.getSigner();
          const addr = await signer.getAddress();
          setAddress(addr);

          // Fetch ENS name from backend (Ethereum mainnet)
          try {
            const backendUrl = import.meta.env.VITE_SWAP_BACKEND_URL || 'http://localhost:9114';
            const response = await fetch(`${backendUrl}/swap/ens/${addr}`);
            if (response.ok) {
              const data = await response.json();
              if (data.ensName) {
                setEnsName(data.ensName);
                console.log('ENS name resolved:', data.ensName, data.cached ? '(cached)' : '(fresh)');
              } else {
                setEnsName(null);
              }
            }
          } catch (error) {
            console.log('ENS lookup failed:', error);
            setEnsName(null);
          }

          onConnect(addr, ethersProvider);
        } else {
          if (!isSubscribed) return;
          setAddress(null);
          setEnsName(null);
          onDisconnect();
        }
      } catch (error) {
        console.error('Error in handleAccountChange:', error);
      } finally {
        isHandling = false;
      }
    };

    // Subscribe to provider changes
    const unsubscribe = modal.subscribeProvider(handleAccountChange);

    // Check initial connection state only if already connected
    if (modal.getIsConnected()) {
      handleAccountChange();
    }

    return () => {
      isSubscribed = false;
      if (unsubscribe) unsubscribe();
    };
  }, [onConnect, onDisconnect]);

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      await modal.open();
    } catch (error) {
      console.error('Wallet connection failed:', error);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await modal.disconnect();
    } catch (error) {
      console.error('Disconnect error:', error);
    }
    setAddress(null);
    setEnsName(null);
    onDisconnect();
  };

  if (address) {
    return (
      <div className="flex items-center gap-4">
        <div className="text-sm text-gray-600">
          {ensName || `${address.slice(0, 6)}...${address.slice(-4)}`}
        </div>
        <button
          onClick={handleDisconnect}
          className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
        >
          Disconnect
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={handleConnect}
      disabled={isConnecting}
      className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-blue-600 transition disabled:opacity-50"
    >
      {isConnecting ? 'Connecting...' : 'Connect Wallet'}
    </button>
  );
}
